import React, { useEffect, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Search,
  X,
  Filter,
  Calendar,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  Package,
  AlertCircle,
  Eye,
  Send,
  Clock,
  MapPin,
  Phone,
  Mail,
  User,
  Calendar as CalendarIcon,
  Edit,
  LayoutGrid,
  List,
} from "lucide-react";
import Pagination from "../../components/Pagination";
import { useAppSelector } from "../../store/hooks";
// AlterCPA removed for Greek-CC
// import {
//   getAccountById,
// } from "../../store/slices/altercpaSlice";
import {
  Order,
  CountryPagination,
  CountrySettings,
  User as UserType,
} from "./types";
import {
  getCountryFlag,
  getShippingCompanyForCountry,
  formatPhoneNumber,
  getAlterCPAStatus,
  getPostOfficeStatus,
} from "./utils";
import PostOfficeSelector from "./PostOfficeSelector";
import EditOrderModal from "./EditOrderModal";
// BEX modal removed for Greek-CC (Greece uses ACS only)
// import BexEditOrderModal from "./BexEditOrderModal";
import PickupDateModal from "./PickupDateModal";
import ViewAllSelectionModal from "./ViewAllSelectionModal";
import { API_BASE_URL } from "../../utils/apiConfig";

interface Props {
  country: string;
  settings: any;
  refreshKey: number;
}


// Helper function to parse MongoDB date objects or regular dates
const parseDate = (dateValue: any): Date | null => {
  if (!dateValue) return null;
  
  // Handle MongoDB date objects like {"$date": "2025-10-16T10:12:15.370Z"}
  if (typeof dateValue === 'object' && dateValue !== null) {
    if (dateValue.$date) {
      return new Date(dateValue.$date);
    }
    // Handle Date objects directly
    if (dateValue instanceof Date) {
      return dateValue;
    }
  }
  
  // Handle regular date strings
  if (typeof dateValue === 'string') {
    const parsed = new Date(dateValue);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  
  // Handle Unix timestamps
  if (typeof dateValue === 'number') {
    // Handle Unix timestamps - if it's less than 13 digits, it's in seconds and needs to be converted to milliseconds
    // If it's 13 digits or more, it's already in milliseconds
    return new Date(dateValue < 10000000000 ? dateValue * 1000 : dateValue);
  }
  
  return null;
};

// Helper function to determine an order's stage
// Since we use only one post office, both with and without post office are "ready-for-shipping"
const getOrderStage = (order: Order): "ready-for-shipping" | "shipped" => {
  if (order.trackingId) return "shipped";
  return "ready-for-shipping"; // Both with and without post office are ready for shipping
};

// Helper function to get the best available timestamp for display and sorting
const getBestTimestamp = (order: Order): number | null => {
  // Priority order: packingTimestamp > altercpaPackingStateTime > lastUpdated > done
  
  // Check packingTimestamp first (can be Unix timestamp or MongoDB date object)
  if (order.packingTimestamp) {
    const date = parseDate(order.packingTimestamp);
    if (date && !isNaN(date.getTime())) {
      return date.getTime();
    }
  }
  
  // Check altercpaPackingStateTime (ISO date string or MongoDB date object)
  if (order.altercpaPackingStateTime) {
    const date = parseDate(order.altercpaPackingStateTime);
    if (date && !isNaN(date.getTime())) {
      return date.getTime();
    }
  }
  
  // Check lastUpdated (ISO date string or MongoDB date object)
  if (order.lastUpdated) {
    const date = parseDate(order.lastUpdated);
    if (date && !isNaN(date.getTime())) {
      return date.getTime();
    }
  }
  
  // For manual orders, check done timestamp as fallback
  if (order.done) {
    const date = parseDate(order.done);
    if (date && !isNaN(date.getTime())) {
      return date.getTime();
    }
  }
  
  return null;
};

export interface CountrySectionProps {
  country: string;
  currentUser?: UserType;
  isExpanded: boolean;
  onToggleExpansion: (country: string) => void;
  settings: CountrySettings[string];
  pagination: CountryPagination[string];
  selectedOrders: Set<number>;
  selectedOrdersMeta: Record<number, Order>;
  getSelectedOrdersForCountry: (country: string) => Order[];
  sendingToShipping: Set<number>;
  isBulkSelectionMode: boolean;
  onToggleOrderSelection: (orderId: number, order?: Order) => void;
  onToggleCountryOrdersSelection: (country: string, selectAll: boolean) => void;
  currentSelectionStage: "ready-for-shipping" | "shipped" | "mixed" | null;
  onUpdateStage: (stage: "ready-for-shipping" | "shipped" | "mixed" | null) => void;
  onRefreshSelectedOrdersMeta: (country: string, freshOrders?: Order[]) => void;
  onStartBulkOperation?: (country: string) => void;
  onStopBulkOperation?: (country: string) => void;
  pendingNextStageSelections: Set<number>;
  getPendingNextStageOrders: () => Order[];
  onProgressToNextStage: () => void;
  onClearPendingSelections: () => void;
  onSendToBex: (orderId: number, pickupDate: string, notes?: string) => void;
  onSelectPostOffice: (orderId: number, postOffice: string) => Promise<void>;
  onClearPostOffice: (orderId: number) => Promise<void>;
  onSendToShipping: (
    orderId: number,
    postOffice: string,
    pickupDate: string,
    notes?: string
  ) => Promise<void>;
  onPrintLabel: (orderId: number, labelType: "a6" | "a4") => Promise<void>;
  onViewLabel: (orderId: number, labelType: "a6" | "a4") => void;
  onDeleteShipment: (orderId: number) => Promise<void>;
  onEditOrder: (orderId: number, updates: Partial<Order>) => Promise<void>;
  onCountrySearch: (country: string, searchTerm: string) => void;
  onCountrySort: (
    country: string,
    sortBy: string,
    sortOrder: "asc" | "desc" | "none"
  ) => void;
  onCountryFilter: (country: string, statusFilter: string) => void;
  onCountryPageChange: (country: string, page: number) => void;
  onLoadCountryData: (country: string, page: number) => void;
  onUpdateCountrySettings: (
    country: string,
    updates: Partial<CountrySettings[string]>
  ) => void;
  onLoadCountryDataWithFilters: (
    country: string,
    page: number,
    filters: {
      search?: string;
      sortBy?: string;
      sortOrder?: "asc" | "desc" | "none";
      statusFilter?: string;
      sourceFilter?: string;
      periodFilter?: string;
      customStartDate?: string;
      customEndDate?: string;
    }
  ) => void;
  viewMode?: "table" | "cards";
}

const CountrySection: React.FC<CountrySectionProps> = ({
  country,
  currentUser,
  isExpanded,
  onToggleExpansion,
  settings,
  pagination,
  selectedOrders,
  selectedOrdersMeta,
  getSelectedOrdersForCountry,
  sendingToShipping,
  isBulkSelectionMode,
  onToggleOrderSelection,
  onToggleCountryOrdersSelection,
  currentSelectionStage,
  onUpdateStage,
  onRefreshSelectedOrdersMeta,
  onStartBulkOperation,
  onStopBulkOperation,
  pendingNextStageSelections,
  getPendingNextStageOrders,
  onProgressToNextStage,
  onClearPendingSelections,
  onSendToBex,
  onSelectPostOffice,
  onClearPostOffice,
  onSendToShipping,
  onPrintLabel,
  onViewLabel,
  onDeleteShipment,
  onEditOrder,
  onCountrySearch,
  onCountrySort,
  onCountryFilter,
  onCountryPageChange,
  onLoadCountryData,
  onUpdateCountrySettings,
  onLoadCountryDataWithFilters,
  viewMode = "cards",
}) => {
  // AlterCPA removed for Greek-CC
  // const altercpaState = useAppSelector((state) => state.altercpa);
  const shippingCompany = getShippingCompanyForCountry(country);
  const isConnected = shippingCompany?.status === "connected";
  const orders = settings.orders || [];

  // Helper function to get web order data
  const getWebOrderData = (order: Order) => {
    if (!order.webOrderData) return null;
    
    const webData = order.webOrderData;
    
    // Get customer info
    const billingAddr = webData.billing_address || {};
    const shippingAddr = webData.shipping_address || {};
    const addressesAreSame = JSON.stringify(billingAddr) === JSON.stringify(shippingAddr);
    
    const customer = {
      name: order.name,
      email: order.email,
      phone: order.phone,
      billingAddress: {
        address1: billingAddr.address_1 || '',
        address2: billingAddr.address_2 || '',
        city: billingAddr.city || '',
        postcode: billingAddr.postcode || '',
        zone: billingAddr.zone || '',
        country: billingAddr.country || ''
      },
      shippingAddress: addressesAreSame ? null : {
        address1: shippingAddr.address_1 || '',
        address2: shippingAddr.address_2 || '',
        city: shippingAddr.city || '',
        postcode: shippingAddr.postcode || '',
        zone: shippingAddr.zone || '',
        country: shippingAddr.country || ''
      },
      addressesAreSame
    };
    
    // Get paid items (product_price > 0)
    const paidItems = (webData.order_items || [])
      .filter((item: any) => item.product_price > 0)
      .map((item: any) => ({
        productName: item.product_name,
        quantity: item.product_quantity,
        unitPrice: item.product_price,
        total: item.product_total
      }));
    
    // Calculate costs
    const productsCost = paidItems.reduce((sum: number, item: any) => sum + item.total, 0);
    const paymentMethod = webData.payment_method || '';
    const isCashOnDelivery = 
      paymentMethod.toLowerCase().includes('αντικαταβολή') || 
      paymentMethod.toLowerCase().includes('cash on delivery') ||
      paymentMethod.toLowerCase().includes('cod');
    const codFee = isCashOnDelivery ? 2.00 : 0;
    const shippingCost = productsCost >= 40 ? 0 : 5.00;
    const totalCost = productsCost + codFee + shippingCost;
    
    return {
      customer,
      paidItems,
      shippingCost,
      totalCost,
      currency: order.currency || 'EUR'
    };
  };

  // Helper function to format currency
  const formatCurrency = (amount: number, currency: string = "EUR") => {
    return `${amount.toFixed(2)} ${currency}`;
  };

  // Helper function to get TRACKING STATUS badge
  const getACSTrackingStatusBadge = (order: Order) => {
    if (!order.acsTrackingStatus) return null;
    
    const status = order.acsTrackingStatus;
    let statusText: string = status.status || "Unknown";
    let colorClass = "bg-gray-100 text-gray-800";
    
    if (status.deliveryFlag === 1) {
      statusText = "Delivered";
      colorClass = "bg-green-100 text-green-800";
    } else if (status.returnedFlag === 1) {
      statusText = "Returned";
      colorClass = "bg-red-100 text-red-800";
    } else if (status.status === "Transit") {
      statusText = "In Transit";
      colorClass = "bg-yellow-100 text-yellow-800";
    }
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
        {statusText}
      </span>
    );
  };

  // Edit modal state
  const [editModal, setEditModal] = useState<{
    isOpen: boolean;
    order: Order | null;
  }>({
    isOpen: false,
    order: null,
  });

  // Pickup date modal state
  const [pickupDateModal, setPickupDateModal] = useState<{
    isOpen: boolean;
    orderId: number | null;
    orderIds: number[] | null;
    shippingCompany: string;
    isBulk: boolean;
    orders?: Array<{ id: number; price: number; currency: string; source?: string }>;
  }>({
    isOpen: false,
    orderId: null,
    orderIds: null,
    shippingCompany: "",
    isBulk: false,
  });

  // View all selection modal state
  const [viewAllSelectionModal, setViewAllSelectionModal] = useState(false);

  // Bulk shipping progress state (inline in table)
  const [bulkShippingProgress, setBulkShippingProgress] = useState<{
    isProcessing: boolean;
    orderStatus: Record<number, {
      status: "pending" | "processing" | "success" | "error";
      message?: string;
      error?: string;
      trackingNumber?: string;
    }>;
  }>({
    isProcessing: false,
    orderStatus: {},
  });

  // Track orders with success animation (for fade-out effect)
  const [successAnimations, setSuccessAnimations] = useState<Set<number>>(new Set());

  // Use stats from backend (includes period filter and all orders, not just current page)
  const stats = settings.stats || {
    totalShipments: 0,
    registered: 0,
    inTransit: 0,
    delivered: 0,
    returned: 0,
  };
  const packagingOrders =
    (settings.stats?.packagingOrders as number | undefined) || 0;

  const isSerbiaSection = country.toLowerCase() === "rs";
  const EXTRA_SHIPPING_THRESHOLD_RSD = 4000;
  const EXTRA_SHIPPING_FEE_RSD = 330;

  const normalizeCurrency = (currency?: string) =>
    (currency || (isSerbiaSection ? "RSD" : "")).toString().trim().toUpperCase();

  const formatPriceValue = (value: number, currency: string) => {
    const formatter = new Intl.NumberFormat(undefined, {
      minimumFractionDigits: value % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    });
    return `${formatter.format(value)} ${currency}`.trim();
  };

  const getOrderPricing = (order: Order) => {
    let quantity = Number(order.count) || 1;
    let unitPrice = 0;
    let subtotal = Number(order.price) || 0; // Total price from order
    
    // Calculate from goods array if available
    if (order.goods && order.goods.length > 0) {
      // Total count = sum of all goods counts
      quantity = order.goods.reduce((sum: number, good: any) => sum + (Number(good.count) || 0), 0);
      // Unit price = price from first good (goods[0].price)
      unitPrice = Number(order.goods[0]?.price) || 0;
      // Subtotal = sum of (goods[i].price * goods[i].count) for all goods
      subtotal = order.goods.reduce((sum: number, good: any) => {
        return sum + ((Number(good.price) || 0) * (Number(good.count) || 0));
      }, 0);
    } else {
      // Fallback: calculate unit price from order.price / order.count
      if (quantity > 0) {
        unitPrice = subtotal / quantity;
      }
    }
    
    const currency = normalizeCurrency(order.currency);
    const isDinCurrency = ["RSD", "DIN", "DINAR", "DINARA"].includes(currency);
    const needsExtraShipping =
      isSerbiaSection && isDinCurrency && subtotal > 0 && subtotal < EXTRA_SHIPPING_THRESHOLD_RSD;
    const extraShippingFee = needsExtraShipping ? EXTRA_SHIPPING_FEE_RSD : 0;
    const total = subtotal + extraShippingFee;

    return {
      quantity,
      unitPrice,
      subtotal,
      total,
      currency: currency || (isSerbiaSection ? "RSD" : ""),
      extraShippingFee,
      needsExtraShipping,
    };
  };

  // Bulk post office selection state
  const [showBulkPostOfficeSelector, setShowBulkPostOfficeSelector] =
    useState(false);
  const [bulkPostOffice, setBulkPostOffice] = useState<string>("");
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);

  // Force re-render state
  const [refreshKey, setRefreshKey] = useState(0);

  // Custom date range state
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);
  const [customDateRange, setCustomDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    end: new Date().toISOString().split("T")[0],
  });

  // Applied custom date range (only updates when user clicks "Apply Range")
  const [appliedCustomDateRange, setAppliedCustomDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    end: new Date().toISOString().split("T")[0],
  });

  // Delivered stats for ACS (fetched from acsTrackingStatus)
  const [deliveredStats, setDeliveredStats] = useState<{
    Transit: number;
    Delivered: number;
    Returned: number;
    Complicated: number;
  } | null>(null);

  // Calculate filtered stats from displayed orders using the same logic as ACS delivered-stats
  // Use shippingStats from backend instead of calculating locally
  // Backend provides accurate stats based on ACS flags
  const filteredStats = React.useMemo(() => {
    const shippingStats = settings.stats || {};
    
    // Map backend stats to frontend display format
    return {
      Transit: shippingStats.inTransit || 0,
      Delivered: shippingStats.delivered || 0,
      Returned: shippingStats.returned || 0,
      Complicated: 0, // Calculate complicated from displayed orders
    };
  }, [settings.stats]);
  
  // Calculate complicated count from displayed orders
  const complicatedCount = React.useMemo(() => {
    return orders.filter(order => order.acsTrackingStatus?.isComplicated).length;
  }, [orders]);


  // Fetch delivered stats for Greece when expanded or period changes
  React.useEffect(() => {
    if (isExpanded && country.toLowerCase() === "gr") {
      fetchDeliveredStatsForCountry();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded, settings.periodFilter, country, appliedCustomDateRange.start, appliedCustomDateRange.end]);

  // Force re-render when settings change to ensure UI updates
  React.useEffect(() => {
    console.log(`🔄 [UI REFRESH] ${country} settings changed:`, {
      periodFilter: settings.periodFilter,
      ordersCount: (settings.orders || []).length,
      paginationTotalCount: pagination.totalCount,
      stats: settings.stats
    });
  }, [settings.periodFilter, settings.orders, settings.stats, pagination.totalCount, country]);

  // Auto-refresh metadata when orders change after bulk operations
  React.useEffect(() => {
    if (selectedOrders.size > 0 && settings.orders && settings.orders.length > 0) {
      // Check if any selected order has updated data
      const hasStaleData = Array.from(selectedOrders).some(orderId => {
        const currentMeta = selectedOrdersMeta[orderId];
        const freshOrder = settings.orders.find((o: Order) => o.id === orderId);
        
        if (!freshOrder || !currentMeta) return false;
        
        // Check if any critical field changed
        const metaChanged = 
          (currentMeta.selectedPostOffice !== freshOrder.selectedPostOffice) ||
          (currentMeta.trackingId !== freshOrder.trackingId);
          
        if (metaChanged) {
          console.log(`🔄 [AUTO-REFRESH] Order ${orderId} data changed, refreshing metadata`);
        }
        
        return metaChanged;
      });
      
      if (hasStaleData) {
        onRefreshSelectedOrdersMeta(country, settings.orders);
      }
    }
  }, [settings.orders, selectedOrders, selectedOrdersMeta, country, onRefreshSelectedOrdersMeta]);

  const fetchDeliveredStatsForCountry = async () => {
    try {
      // Use the same period calculation as the main data
      const periodToUse = settings.periodFilter || "current_month";

      const params = new URLSearchParams();
      
      // Handle custom date range
      if (periodToUse === "custom") {
        params.append("customStartDate", appliedCustomDateRange.start);
        params.append("customEndDate", appliedCustomDateRange.end);
        params.append("dateRange", "custom");
        console.log(`🔍 [ACS-STATS] Using custom date range for ${country}:`, appliedCustomDateRange.start, "to", appliedCustomDateRange.end);
      } else {
        params.append("dateRange", periodToUse.replace("current_", "this_"));
      }
      
      params.append("country", country.toUpperCase());

      const response = await fetch(
        `${API_BASE_URL}/api/shipping/acs/delivered-stats?${params.toString()}`
      );
      const result = await response.json();

      if (result.success) {
        setDeliveredStats(result.data.summary);
      }
    } catch (error) {
      console.error(`Failed to fetch delivered stats for ${country}:`, error);
    }
  };

  // Memoize selected orders compatibility state to avoid recalculating on every render
  const selectedOrdersCompatibility = React.useMemo(() => {
    // Use cross-page meta snapshot for the current country
    const selectedOrdersList = getSelectedOrdersForCountry(country);

    if (selectedOrdersList.length === 0) {
      return { type: "none", canSelect: true, count: 0, hasPickupList: false };
    }

    const allShipped = selectedOrdersList.every((order) => order.trackingId);
    const allReadyForShipping = selectedOrdersList.every((order) => !order.trackingId);

    // Check if any order has a pickup list issued
    const hasPickupList = selectedOrdersList.some(
      (order) => order.pickupListNumber || order.pickupListIssued
    );

    // If all orders have tracking IDs (shipped), they can be bulk deleted/previewed
    // BUT only if they don't have pickup lists issued yet
    if (allShipped)
      return {
        type: "shipped",
        canSelect: true,
        count: selectedOrdersList.length,
        hasPickupList: hasPickupList,
      };
    if (allReadyForShipping)
      return {
        type: "ready-for-shipping",
        canSelect: true,
        count: selectedOrdersList.length,
        hasPickupList: false,
      };
    return {
      type: "mixed",
      canSelect: false,
      count: selectedOrdersList.length,
      hasPickupList: false,
    };
  }, [getSelectedOrdersForCountry, country, selectedOrders]);

  // Wrapper to open pickup date modal before shipping
  const handleSendToShipping = async (orderId: number, postOffice: string) => {
    // Find the order to get its price and currency
    const order = orders.find(o => o.id === orderId);
    
    // Greece always uses ACS
    const countryLower = country.toLowerCase();
    const shippingCompany = (countryLower === 'gr' || countryLower === 'greece') ? 'acs' : postOffice;
    
    setPickupDateModal({
      isOpen: true,
      orderId,
      orderIds: null,
      shippingCompany: shippingCompany,
      isBulk: false,
      orders: order ? [{
        id: order.id,
        price: order.price || 0,
        currency: order.currency || 'EUR',
        source: order.source
      }] : [],
    });
    return Promise.resolve();
  };

  // Handle bulk shipping (send to shipping)
  const handleBulkSendToShipping = () => {
    // Get selected orders for this country across all pages
    const selectedOrdersList = getSelectedOrdersForCountry(country);
    const orderIds = selectedOrdersList.map((order) => order.id);

    if (orderIds.length === 0) return;

    // Determine the shipping company
    // Greece uses ACS by default, other countries use selected post office
    const countryLower = country.toLowerCase();
    const shippingCompany = (countryLower === 'gr' || countryLower === 'greece')
      ? 'acs'
      : selectedOrdersList[0].selectedPostOffice?.toLowerCase() || "";

    console.log(
      `🚚 [BULK] Opening pickup date modal for ${orderIds.length} orders to ${shippingCompany}`
    );

    // Open pickup date modal for bulk shipping
    setPickupDateModal({
      isOpen: true,
      orderId: null,
      orderIds: orderIds,
      shippingCompany: shippingCompany,
      isBulk: true,
      orders: selectedOrdersList.map(order => ({
        id: order.id,
        price: order.price || 0,
        currency: order.currency || 'EUR',
        source: order.source
      }))
    });
  };

  // Start bulk shipping with real-time progress updates
  const startBulkShippingAsync = async (
    orderIds: number[],
    pickupDate: string,
    pickupDateNotes?: string,
    priceRounding?: Record<number, boolean>
  ) => {
    try {
      // Start SSE connection
      const response = await fetch(`${API_BASE_URL}/api/shipping/acs/bulk-send-async`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          orderIds,
          pickupDate,
          pickupDateNotes,
          priceRounding,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Read the SSE stream
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error("Failed to get response reader");
      }

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              handleBulkShippingEvent(data);
            } catch (err) {
              console.error("Error parsing SSE message:", err);
            }
          }
        }
      }
    } catch (err) {
      console.error("❌ [BULK-SHIPPING] Error:", err);
      
      // Mark all pending orders as error
      setBulkShippingProgress(prev => {
        const newStatus = { ...prev.orderStatus };
        Object.keys(newStatus).forEach(orderId => {
          if (newStatus[Number(orderId)].status === "pending" || newStatus[Number(orderId)].status === "processing") {
            newStatus[Number(orderId)] = {
              status: "error",
              error: err instanceof Error ? err.message : "Failed to process",
            };
          }
        });
        return { isProcessing: false, orderStatus: newStatus };
      });
    }
  };

  // Handle SSE events from bulk shipping
  const handleBulkShippingEvent = (data: any) => {
    console.log("📨 [SSE]", data);

    switch (data.type) {
      case "start":
        console.log(`🚀 Starting bulk shipment for ${data.total} orders`);
        break;

      case "progress":
        setBulkShippingProgress(prev => ({
          ...prev,
          orderStatus: {
            ...prev.orderStatus,
            [data.orderId]: {
              status: data.status,
              message: data.message,
              error: data.error,
              trackingNumber: data.trackingNumber,
            },
          },
        }));
        
        // If successful, add to success animations
        // Tracking numbers are stored in bulkShippingProgress.orderStatus and displayed from there
        if (data.status === "success") {
          setSuccessAnimations(prev => new Set(prev).add(data.orderId));
          
          // Remove from success animations after 4 seconds
          setTimeout(() => {
            setSuccessAnimations(prev => {
              const newSet = new Set(prev);
              newSet.delete(data.orderId);
              return newSet;
            });
          }, 4000);
        }
        break;

      case "complete":
        console.log(`✅ Bulk shipment complete: ${data.successCount} success, ${data.failCount} failed`);
        
        setBulkShippingProgress(prev => ({
          ...prev,
          isProcessing: false,
        }));
        
        // Wait for animations to be visible (3-4 seconds), then reload data
        setTimeout(async () => {
          console.log('🔄 [BULK-ASYNC] Reloading data after bulk completion...');
          
          // Reload to get fresh tracking numbers and status from backend
          await onLoadCountryDataWithFilters(country, pagination.currentPage, {
            search: settings.search,
            sortBy: settings.sortBy,
            sortOrder: settings.sortOrder,
            statusFilter: settings.statusFilter,
            sourceFilter: settings.sourceFilter,
            periodFilter: settings.periodFilter,
          });
          
          console.log('✅ [BULK-ASYNC] Data reloaded - tracking numbers now from database');
          
          // Clear selection after reload
          onToggleCountryOrdersSelection(country, false);
          
          // Update stage to "shipped" if all orders were successful
          if (data.successCount > 0 && data.failCount === 0) {
            onUpdateStage("shipped");
            console.log('✅ [BULK-ASYNC] All orders shipped successfully, stage updated to "shipped"');
          }
          
          // Clear progress state (but keep tracking numbers visible until reload completes)
          setTimeout(() => {
            setBulkShippingProgress({
              isProcessing: false,
              orderStatus: {},
            });
          }, 1000);
        }, 4000);
        break;

      case "error":
        console.error("❌ Bulk shipping error:", data.error);
        setBulkShippingProgress(prev => ({
          ...prev,
          isProcessing: false,
        }));
        break;
    }
  };

  // Handle bulk delete (for shipped orders)
  const handleBulkDelete = async () => {
    const selectedOrdersList = orders.filter((order) =>
      selectedOrders.has(order.id)
    );

    if (selectedOrdersList.length === 0) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedOrdersList.length} shipment${
        selectedOrdersList.length !== 1 ? "s" : ""
      }?\n\nThis action cannot be undone.`
    );

    if (!confirmed) return;

    setIsBulkProcessing(true);
    try {
      console.log(
        `🗑️ [BULK DELETE] Deleting ${selectedOrdersList.length} shipments`
      );

      let successCount = 0;
      let failCount = 0;
      const errors: Array<{ orderId: number; error: string }> = [];

      // Process each order sequentially WITHOUT reloading after each one
      for (const order of selectedOrdersList) {
        try {
          // Call the delete API directly instead of using onDeleteShipment
          // to avoid multiple table refreshes
          const shippingCompany = order.shippingCompany || "";
          let deleteEndpoint = "";
          
          if (shippingCompany === "acs") {
            deleteEndpoint = `${API_BASE_URL}/api/shipping/acs/delete/${order.trackingId}`;
          } else if (shippingCompany === "easymail") {
            deleteEndpoint = `${API_BASE_URL}/api/shipping/easymail/delete/${order.trackingId}`;
          } else if (shippingCompany === "bex") {
            deleteEndpoint = `${API_BASE_URL}/api/shipping/bex/delete/${order.trackingId}`;
          } else {
            throw new Error(`Deletion not supported for ${shippingCompany}`);
          }

          const response = await fetch(deleteEndpoint, { method: "DELETE" });
          const result = await response.json();

          if (result.success) {
            successCount++;
            console.log(`✅ [BULK DELETE] Deleted shipment for order ${order.id}`);
          } else {
            throw new Error(result.error || "Failed to delete");
          }
          
          // Small delay between requests
          await new Promise((resolve) => setTimeout(resolve, 300));
        } catch (error: any) {
          failCount++;
          errors.push({ orderId: order.id, error: error.message || "Unknown error" });
          console.error(
            `❌ [BULK DELETE] Failed to delete shipment for order ${order.id}:`,
            error
          );
        }
      }

      console.log(
        `✅ [BULK DELETE] Completed: ${successCount} successful, ${failCount} failed`
      );

      // Reload data ONCE after all deletions
      await onLoadCountryDataWithFilters(country, pagination.currentPage, {
        search: settings.search,
        sortBy: settings.sortBy,
        sortOrder: settings.sortOrder,
        statusFilter: settings.statusFilter,
        sourceFilter: settings.sourceFilter,
        periodFilter: settings.periodFilter,
      });

      // Clear selection after deletion
      onToggleCountryOrdersSelection(country, false);

      if (failCount > 0) {
        const errorDetails = errors.map(e => `Order #${e.orderId}: ${e.error}`).join('\n');
        alert(
          `Deletion Results:\n✅ Successfully deleted: ${successCount} shipment${
            successCount !== 1 ? "s" : ""
          }\n❌ Failed: ${failCount}\n\nErrors:\n${errorDetails}`
        );
      }
    } catch (error) {
      console.error("❌ [BULK DELETE] Bulk delete failed:", error);
      alert("Error deleting shipments. Please try again.");
    } finally {
      setIsBulkProcessing(false);
    }
  };

  // Handle bulk preview (supports unlimited orders, batches of 9)
  const handleBulkPreview = async () => {
    // Get all selected orders for this country across all pages
    const selectedOrdersList = getSelectedOrdersForCountry(country);

    if (selectedOrdersList.length === 0) {
      alert("Please select at least 1 order to preview.");
      return;
    }

    // Get tracking IDs from selected orders
    const trackingIds = selectedOrdersList
      .map((order) => order.trackingId)
      .filter((id) => id);

    if (trackingIds.length !== selectedOrdersList.length) {
      alert("All selected orders must have tracking numbers.");
      return;
    }

    // Check if any order already has a pickup list issued
    const ordersWithPickupList = selectedOrdersList.filter(
      (order) => order.pickupListNumber || order.pickupListIssued
    );

    if (ordersWithPickupList.length > 0) {
      const orderIds = ordersWithPickupList.map((o) => `#${o.id}`).join(", ");
      alert(
        `Cannot preview labels - pickup list already issued for:\n${orderIds}\n\nOrders with pickup lists cannot be previewed individually.`
      );
      return;
    }

    // Get shipping company from first order
    const shippingCompany = selectedOrdersList[0].shippingCompany || "";

    console.log(
      `📄 [BULK PREVIEW] Generating preview for ${trackingIds.length} orders in batches of 9`
    );

    setIsBulkProcessing(true);
    try {
      // Process in batches of 9 (ACS API limit)
      const batchSize = 9;
      let pdfCount = 0;

      for (let i = 0; i < trackingIds.length; i += batchSize) {
        const batch = trackingIds.slice(i, i + batchSize);
        
        // Build query string with tracking IDs for this batch (max 9)
        const trackingParams = batch
          .map(
            (id, index) =>
              `trackingId${index + 1}=${encodeURIComponent(id || "")}`
          )
          .join("&");
        const apiUrl = `${API_BASE_URL}/api/shipping/${shippingCompany}/print-bulk?${trackingParams}&printType=2&startPosition=1`;

        console.log(`📄 [BULK PREVIEW] Batch ${i / batchSize + 1}: ${batch.length} orders`);

        const response = await fetch(apiUrl, {
          method: "GET",
        });

        const result = await response.json();

        if (result.success) {
          // Extract PDF base64 from response
          const pdfBase64 =
            result.data?.pdfBase64 || result.data?.base64 || result.pdfBase64;

          if (pdfBase64) {
            console.log(`✅ [BULK PREVIEW] Batch ${i / batchSize + 1} PDF generated`);

            // Download the PDF
            const link = document.createElement("a");
            link.href = `data:application/pdf;base64,${pdfBase64}`;
            link.download = `labels_batch_${i / batchSize + 1}_${batch[0]}.pdf`;
            link.target = '_blank'; // Open in new tab
            link.click();
            
            pdfCount++;
          } else {
            console.error("❌ [BULK PREVIEW] PDF data not found for batch", i / batchSize + 1);
          }
        } else {
          console.error("❌ [BULK PREVIEW] Batch failed:", result.error);
        }
      }

      console.log(`✅ [BULK PREVIEW] Generated ${pdfCount} PDF files`);
      
      if (pdfCount === 0) {
        alert("Failed to generate any PDF files");
      }
    } catch (error) {
      console.error("❌ [BULK PREVIEW] Error:", error);
      alert("Error generating preview. Please try again.");
    } finally {
      setIsBulkProcessing(false);
    }
  };

  // Handle bulk clear post office
  const handleBulkClearPostOffice = React.useCallback(async () => {
    const selectedOrdersList = getSelectedOrdersForCountry(country);
    const orderIds = selectedOrdersList.map((order) => order.id);

    if (orderIds.length === 0) return;

    const confirmed = window.confirm(
      `Are you sure you want to clear the post office for ${orderIds.length} order${
        orderIds.length !== 1 ? "s" : ""
      }?`
    );

    if (!confirmed) return;

    setIsBulkProcessing(true);
    
    // Start tracking bulk operation to suppress conflict modal
    onStartBulkOperation?.(country);
    
    // IMMEDIATELY update metadata to reflect the new stage (before calling onUpdateStage)
    // This prevents the "mixed" message from showing
    // Use selectedOrdersMeta to ensure we have complete order data (including address, etc.)
    const updatedMeta = { ...selectedOrdersMeta };
    orderIds.forEach(orderId => {
      if (updatedMeta[orderId]) {
        updatedMeta[orderId] = {
          ...updatedMeta[orderId], // Preserve ALL fields (address, customer data, etc.)
          selectedPostOffice: undefined,
          postOfficeSelectedAt: undefined,
        };
      }
    });
    onRefreshSelectedOrdersMeta(country, Object.values(updatedMeta));
    console.log('🔄 [BULK] Updated metadata immediately to "ready-for-shipping" with ALL fields preserved');
    
    // Update stage
    onUpdateStage("ready-for-shipping");
    console.log('🔄 [BULK] Stage updated to "ready-for-shipping" immediately');
    
    try {
      console.log(
        `🗑️ [BULK] Clearing post office for ${orderIds.length} orders:`,
        orderIds
      );

      // Clear post office for each order
      for (const orderId of orderIds) {
        try {
          await onClearPostOffice(orderId);
          console.log(
            `✅ [BULK] Cleared post office for order ${orderId}`
          );
          // Small delay between requests
          await new Promise((resolve) => setTimeout(resolve, 200));
        } catch (error) {
          console.error(
            `❌ [BULK] Failed to clear post office for order ${orderId}:`,
            error
          );
        }
      }

      console.log(`✅ [BULK] Completed clearing post office for ${orderIds.length} orders`);

      // Get all affected countries from selected orders
      const affectedCountries = new Set<string>();
      selectedOrdersList.forEach(order => {
        affectedCountries.add(order.country?.toLowerCase() || '');
      });

      // Reload data for all affected countries but keep current page
      console.log(`🔄 [BULK] Reloading data for ${affectedCountries.size} countries on page ${pagination.currentPage}`);
      for (const affectedCountry of Array.from(affectedCountries)) {
        // Check if this is the current country, preserve page number
        const targetPage = affectedCountry === country.toLowerCase() ? pagination.currentPage : 1;
        await onLoadCountryDataWithFilters(affectedCountry, targetPage, {
          search: settings.search,
          sortBy: settings.sortBy,
          sortOrder: settings.sortOrder,
          statusFilter: settings.statusFilter,
          sourceFilter: settings.sourceFilter,
          periodFilter: settings.periodFilter,
        });
      }

      console.log('✅ [BULK] Data reloaded and stage is "ready-for-shipping"');
    } catch (error) {
      console.error("❌ [BULK] Bulk clear post office failed:", error);
      alert("Error clearing post office. Please try again.");
    } finally {
      // Stop tracking bulk operation
      onStopBulkOperation?.(country);
      setIsBulkProcessing(false);
    }
  }, [getSelectedOrdersForCountry, country, onClearPostOffice, onLoadCountryDataWithFilters, onUpdateStage, onStartBulkOperation, onStopBulkOperation, onRefreshSelectedOrdersMeta, settings, pagination, selectedOrdersMeta]);

  // Handle bulk post office selection
  const handleBulkPostOfficeSelect = async () => {
    if (!bulkPostOffice) return;

    setIsBulkProcessing(true);
    
    // Start tracking bulk operation to suppress conflict modal
    onStartBulkOperation?.(country);
    
    // IMMEDIATELY update metadata to reflect the new stage
    // Use selectedOrdersMeta to ensure we have complete order data (including address, etc.)
    const updatedMeta = { ...selectedOrdersMeta };
    const selectedOrderIds = Array.from(selectedOrders);
    selectedOrderIds.forEach(orderId => {
      if (updatedMeta[orderId]) {
        updatedMeta[orderId] = {
          ...updatedMeta[orderId], // Preserve ALL fields (address, customer data, etc.)
          selectedPostOffice: bulkPostOffice,
          postOfficeSelectedAt: new Date().toISOString(),
        };
      }
    });
    onRefreshSelectedOrdersMeta(country, Object.values(updatedMeta));
    console.log('🔄 [BULK] Updated metadata immediately to "ready-for-shipping" with ALL fields preserved');
    
    // Update stage
    onUpdateStage("ready-for-shipping");
    console.log('🔄 [BULK] Stage updated to "ready-for-shipping" immediately');
    
    try {
      const selectedOrderIds = Array.from(selectedOrders);

      console.log(
        `📮 [BULK] Selecting post office ${bulkPostOffice} for ${selectedOrderIds.length} orders:`,
        selectedOrderIds
      );

      // Call backend API with bulk orderIds
      const response = await fetch(
        `${API_BASE_URL}/api/shipping/select-post-office`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            orderIds: selectedOrderIds,
            postOffice: bulkPostOffice,
          }),
        }
      );

      const result = await response.json();

      if (result.success || result.data?.successful?.length > 0) {
        console.log(
          `✅ [BULK] Successfully selected post office for ${result.data.successful.length} orders`
        );

        if (result.data?.failed?.length > 0) {
          console.warn(
            `⚠️ [BULK] Failed for ${result.data.failed.length} orders:`,
            result.data.failed
          );
          alert(
            `Post office selected for ${result.data.successful.length} orders.\n${result.data.failed.length} failed - check console for details.`
          );
        }

        // Update selectedOrdersMeta with fresh data from API response
        if (result.data?.successful) {
          const updatedMeta = { ...selectedOrdersMeta };
          result.data.successful.forEach((successfulOrder: any) => {
            const existingOrder = updatedMeta[successfulOrder.orderId];
            if (existingOrder) {
              updatedMeta[successfulOrder.orderId] = {
                ...existingOrder,
                selectedPostOffice: successfulOrder.selectedPostOffice,
                postOfficeSelectedAt: successfulOrder.postOfficeSelectedAt,
              };
            }
          });
          onRefreshSelectedOrdersMeta(country, Object.values(updatedMeta));
          console.log(`✅ [BULK] Updated metadata for ${result.data.successful.length} orders from API response`);
        }

        // Get all affected countries from selected orders
        const selectedOrdersList = getSelectedOrdersForCountry(country);
        const affectedCountries = new Set<string>();
        selectedOrdersList.forEach(order => {
          affectedCountries.add(order.country?.toLowerCase() || '');
        });

        // Reload data for all affected countries but keep current page
        console.log(`🔄 [BULK] Reloading data for ${affectedCountries.size} countries on page ${pagination.currentPage}`);
        for (const affectedCountry of Array.from(affectedCountries)) {
          // Check if this is the current country, preserve page number
          const targetPage = affectedCountry === country.toLowerCase() ? pagination.currentPage : 1;
          await onLoadCountryDataWithFilters(affectedCountry, targetPage, {
            search: settings.search,
            sortBy: settings.sortBy,
            sortOrder: settings.sortOrder,
            statusFilter: settings.statusFilter,
            sourceFilter: settings.sourceFilter,
            periodFilter: settings.periodFilter,
          });
        }

        // DON'T clear selection - keep them selected
        // Metadata will be auto-refreshed by useEffect when settings.orders updates
        
        console.log('✅ [BULK] Post office selected, stage is already "ready-for-shipping".');
        
        setShowBulkPostOfficeSelector(false);
        setBulkPostOffice("");
      } else {
        console.error("❌ [BULK] All orders failed:", result);
        alert(
          `Failed to select post office: ${result.error || "Unknown error"}`
        );
      }
    } catch (error) {
      console.error("❌ [BULK] Bulk post office selection failed:", error);
      alert("Error selecting post office for orders. Please try again.");
    } finally {
      // Stop tracking bulk operation
      onStopBulkOperation?.(country);
      setIsBulkProcessing(false);
    }
  };

  // Local view mode state (defaults to table on desktop, cards on mobile)
  const [localViewMode, setLocalViewMode] = useState<"table" | "cards">(() => {
    // Default based on screen size: table for desktop (>= 640px), cards for mobile
    // If viewMode prop is explicitly provided and different from default, use it
    const screenBasedDefault = window.innerWidth >= 640 ? "table" : "cards";
    // If prop is explicitly set to something other than the default, use it
    // Otherwise use screen-based default
    return viewMode !== "cards" ? viewMode : screenBasedDefault;
  });
  
  // Update local view mode when prop changes (but respect user's manual selection)
  React.useEffect(() => {
    // Only update from prop if it's explicitly set to a different value
    // This allows the prop to override but won't reset user's manual selection
    if (viewMode !== "cards") {
      setLocalViewMode(viewMode);
    }
  }, [viewMode]);

  const [maxWidth, setMaxWidth] = useState<string>("100%");

  useEffect(() => {
    const handleResize = () => {
      const windowWidth = window.innerWidth;
      
      // Calculate padding based on breakpoints (matching parent: px-4 sm:px-6 lg:px-8)
      // px-4 = 16px (1rem), sm:px-6 = 24px (1.5rem), lg:px-8 = 32px (2rem)
      let pagePadding = 20; // default mobile padding (px-4)
      if (windowWidth >= 640) { // sm breakpoint
        pagePadding = 28; // sm:px-6
      }
      if (windowWidth >= 1024) { // lg breakpoint
        pagePadding = 36; // lg:px-8
      }
      
      // Sidebar only exists on lg+ screens (1024px+) and is 256px wide (w-64)
      const sideMenuWidth = windowWidth >= 1024 ? 256 : 0;
      
      // Total horizontal padding (left + right)
      const totalPadding = pagePadding * 2;
      
      const newWidth = windowWidth - sideMenuWidth - totalPadding;
      setMaxWidth(`${newWidth}px`);
    };

    handleResize(); // initial
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  return (
    <div
      key={`${country}-${settings.periodFilter}-${(settings.orders || []).length}-${refreshKey}`}
      className="bg-white rounded-lg shadow mb-6 overflow-hidden w-full max-w-full"
      style={{ maxWidth }}
    >
      {/* Country Header - Clickable */}
      <div
        className={`px-4 sm:px-6 py-4 border-b border-gray-200 cursor-pointer transition-colors hover:bg-opacity-80 ${
          !isConnected ? "bg-red-50 border-red-200" : "bg-gray-50"
        }`}
        onClick={() => onToggleExpansion(country)}
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <div className="flex items-center space-x-2 flex-shrink-0">
              {isExpanded ? (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-500" />
              )}
              <span className="text-2xl">{getCountryFlag(country)}</span>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                {country.toUpperCase()} - {pagination.totalCount} Orders
              </h3>
              {pagination.loading && (
                <div className="flex items-center mt-1">
                  <RefreshCw className="w-3 h-3 animate-spin text-blue-600 mr-1" />
                  <span className="text-xs text-blue-600">Loading...</span>
                </div>
              )}
            </div>
          </div>
          {/* Buttons Row - Multiple rows on mobile, flex-end aligned, full text */}
          <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3 flex-shrink-0 w-full sm:w-auto">
            {/* Statistics Cards */}
            <div className="flex items-center space-x-2 bg-gray-100 rounded-lg px-2 sm:px-3 py-2">
              <Package className="w-4 h-4 flex-shrink-0" />
              <span className="text-xs sm:text-sm">
                {packagingOrders} Packaging Orders
              </span>
            </div>

            {/* Refresh Button */}
            <button
              onClick={(e) => {
                e.stopPropagation(); // Prevent country expansion toggle
                // Use onLoadCountryDataWithFilters to preserve all active filters including custom date range
                const filters: any = {
                  search: settings.search,
                  sortBy: settings.sortBy,
                  sortOrder: settings.sortOrder,
                  statusFilter: settings.statusFilter,
                  sourceFilter: settings.sourceFilter,
                  periodFilter: settings.periodFilter,
                };
                
                // Add custom date range if custom period is selected
                if (settings.periodFilter === "custom") {
                  filters.customStartDate = appliedCustomDateRange.start;
                  filters.customEndDate = appliedCustomDateRange.end;
                }
                
                onLoadCountryDataWithFilters(country, pagination.currentPage, filters);
              }}
              disabled={pagination.loading}
              className="inline-flex items-center px-3 sm:px-4 py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md shadow-sm text-white bg-[#264a3e] hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw
                className={`w-3 h-3 sm:w-4 sm:h-4 ${
                  pagination.loading ? "animate-spin" : ""
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Orders Table - Collapsible */}
      {isExpanded && (
        <>
          {/* Search Indicator */}
          {settings.search && (
            <div className="px-6 py-2 bg-blue-50 border-l-4 border-blue-400">
              <div className="flex items-center justify-between">
                <p className="text-sm text-blue-800">
                  🔍 Showing results for: "{settings.search}"
                </p>
                <span className="text-xs text-blue-600">
                  {(settings.orders || []).length} result
                  {(settings.orders || []).length !== 1 ? "s" : ""}
                </span>
              </div>
            </div>
          )}

          {/* Table Header with Search, Filter, and Sort */}
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50 w-full max-w-full">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between mb-4 gap-3 sm:gap-4">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 flex-wrap">
                {/* Search by Order ID, Customer Name, or Tracking */}
                <div className="flex items-center space-x-2 w-full sm:w-auto">
                  <div className="relative flex-1 sm:flex-initial">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by Order ID, Name, or Tracking..."
                      value={settings.search}
                      onChange={(e) => {
                        const newSearchValue = e.target.value;
                        onUpdateCountrySettings(country, {
                          search: newSearchValue,
                        });

                        // Auto-reload when search is cleared
                        if (newSearchValue === "") {
                          onCountrySearch(country, "");
                        }
                      }}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          onCountrySearch(country, settings.search);
                        }
                      }}
                      className="border border-gray-300 rounded-md pl-10 pr-24 sm:pr-24 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-80"
                    />
                    {settings.search && (
                      <button
                        onClick={() => {
                          onUpdateCountrySettings(country, { search: "" });
                          onCountrySearch(country, "");
                        }}
                        className="absolute right-12 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none z-10"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                    {/* Search Button - Icon only, on right side of input */}
                    <button
                      onClick={() => onCountrySearch(country, settings.search)}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white p-1.5 rounded-md transition-colors hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#264a3e] z-10"
                      style={{ backgroundColor: "#264a3e" }}
                      title="Search"
                    >
                      <Search className="w-4 h-4" />
                    </button>
                  </div>
                  {/* Card/Table View Toggle - Icon only */}
                  <button
                    onClick={() => setLocalViewMode(localViewMode === "table" ? "cards" : "table")}
                    className="inline-flex items-center justify-center p-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors touch-manipulation flex-shrink-0"
                    title={localViewMode === "table" ? "Switch to card view" : "Switch to table view"}
                  >
                    {localViewMode === "table" ? (
                      <LayoutGrid className="w-4 h-4" />
                    ) : (
                      <List className="w-4 h-4" />
                    )}
                  </button>
                </div>
                
                {/* Status Filter */}
                <div className="flex items-center space-x-2 w-full sm:w-auto">
                  <Filter className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <select
                    value={settings.statusFilter || "all"}
                    onChange={(e) => {
                      onCountryFilter(country, e.target.value);
                    }}
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-auto"
                  >
                    <option value="all">All Statuses</option>
                    <option value="not-shipped">Not Shipped</option>
                    <option value="5">Approved Cancelled</option>
                    <option value="6">Packaging</option>
                    <option value="selected-for-post-office">Post Office Selected</option>
                    <option value="7">Sending</option>
                    <option value="in-transit">In Transit</option>
                    <option value="delivered">Delivered</option>
                    <option value="returned">Returned</option>
                  </select>
                </div>

                {/* Period Filter */}
                <div className="flex items-center space-x-2 w-full sm:w-auto">
                  <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <select
                    value={settings.periodFilter || "current_month"}
                    onChange={(e) => {
                      const newPeriodFilter = e.target.value;
                      console.log(
                        `🔍 [PERIOD] Changing ${country} period filter from "${settings.periodFilter}" to "${newPeriodFilter}"`
                      );
                      
                      // Show/hide custom date range selector
                      setShowCustomDateRange(newPeriodFilter === "custom");
                      
                      // Update settings first
                      onUpdateCountrySettings(country, {
                        periodFilter: newPeriodFilter,
                      });
                      
                      // Use the new period filter value directly instead of relying on updated state
                      console.log(`🔄 [PERIOD] Auto-triggering refresh for ${country} with new period: ${newPeriodFilter}`);
                      
                      // Build filters object with custom date range if needed
                      const filters: any = {
                        search: settings.search,
                        sortBy: settings.sortBy,
                        sortOrder: settings.sortOrder,
                        statusFilter: settings.statusFilter,
                        sourceFilter: settings.sourceFilter,
                        periodFilter: newPeriodFilter,
                      };
                      
                      // Add custom date range if custom period is selected
                      if (newPeriodFilter === "custom") {
                        filters.customStartDate = appliedCustomDateRange.start;
                        filters.customEndDate = appliedCustomDateRange.end;
                      }
                      
                      // Use the correct function with filters
                      onLoadCountryDataWithFilters(country, 1, filters);
                      
                      // Force re-render after a short delay
                      setTimeout(() => {
                        setRefreshKey(prev => prev + 1);
                        console.log(`🔄 [PERIOD] Force refresh triggered for ${country}`);
                      }, 200);
                    }}
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-auto"
                  >
                    <option value="today">Today</option>
                    <option value="current_week">This Week</option>
                    <option value="current_month">This Month</option>
                    <option value="current_year">This Year</option>
                    <option value="custom">Custom Range</option>
                    <option value="all">All Time</option>
                  </select>
                </div>
              </div>

              {/* <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-600">
                  {orders.length} orders
                  {settings.search && (
                    <span className="text-blue-600 ml-2">
                      (filtered from {pagination.totalCount})
                    </span>
                  )}
                </div>
                <button
                  onClick={() =>
                    onLoadCountryData(country, pagination.currentPage)
                  }
                  disabled={pagination.loading}
                  className="flex items-center space-x-1 text-white px-3 py-1 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
                  style={{ backgroundColor: "#264a3e" }}
                >
                  <RefreshCw
                    className={`w-4 h-4 ${
                      pagination.loading ? "animate-spin" : ""
                    }`}
                  />
                  <span className="text-sm">Refresh</span>
                </button>
              </div> */}
            </div>
          </div>

          {/* Custom Date Range Selector */}
          {showCustomDateRange && (
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  Custom Date Range:
                </label>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 flex-1">
                  <input
                    type="date"
                    value={customDateRange.start}
                    onChange={(e) =>
                      setCustomDateRange((prev) => ({
                        ...prev,
                        start: e.target.value,
                      }))
                    }
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-[#264a3e] w-full sm:w-auto"
                  />
                  <span className="text-gray-500 text-center sm:text-left">to</span>
                  <input
                    type="date"
                    value={customDateRange.end}
                    onChange={(e) =>
                      setCustomDateRange((prev) => ({
                        ...prev,
                        end: e.target.value,
                      }))
                    }
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-[#264a3e] w-full sm:w-auto"
                  />
                  <button
                    onClick={() => {
                      // Apply the custom date range
                      setAppliedCustomDateRange(customDateRange);
                      
                      // Trigger reload with custom date range
                      onLoadCountryDataWithFilters(country, 1, {
                        search: settings.search,
                        sortBy: settings.sortBy,
                        sortOrder: settings.sortOrder,
                        statusFilter: settings.statusFilter,
                        sourceFilter: settings.sourceFilter,
                        periodFilter: "custom",
                        customStartDate: customDateRange.start,
                        customEndDate: customDateRange.end,
                      });
                      
                      // Force re-render
                      setTimeout(() => {
                        setRefreshKey(prev => prev + 1);
                      }, 200);
                    }}
                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#264a3e] hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors w-full sm:w-auto"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Apply Range
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Pending Next Stage Selections Banner */}
          {pendingNextStageSelections.size > 0 && (
            <div className="px-6 py-4 border-b border-orange-200 bg-orange-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                  <div>
                    <p className="text-sm font-medium text-orange-900">
                      {pendingNextStageSelections.size} order
                      {pendingNextStageSelections.size !== 1 ? "s" : ""} selected for next stage
                    </p>
                    <p className="text-xs text-orange-700 mt-1">
                      Current: <span className="font-medium">{currentSelectionStage}</span> → 
                      Next: <span className="font-medium">
                        {(() => {
                          const pendingOrders = getPendingNextStageOrders();
                          if (pendingOrders.length > 0) {
                            const stage = pendingOrders[0].trackingId 
                              ? "shipped" 
                              : "ready-for-shipping";
                            return stage;
                          }
                          return "unknown";
                        })()}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={onProgressToNextStage}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Complete Current Stage
                  </button>
                  <button
                    onClick={onClearPendingSelections}
                    className="text-orange-600 hover:text-orange-800"
                    title="Dismiss pending selections"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Status Cards for this Country HIDDEN FOR NOW */}
          {/* <div className="px-6 py-4 border-b border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4"> */}
              {/* Total Shipments */}
              {/* <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Truck className="w-6 h-6" style={{ color: "#264a3e" }} />
                  </div>
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-500">
                      Total Shipments
                    </p>
                    <p className="text-lg font-semibold text-gray-900">
                      {filteredStats.Transit +
                        filteredStats.Delivered +
                        filteredStats.Returned}
                    </p>
                  </div>
                </div>
              </div> */}

              {/* Complicated / Registered */}
              {/* <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    {complicatedCount > 0 ? (
                      <AlertTriangle className="w-6 h-6 text-orange-600" />
                    ) : (
                      <Package
                        className="w-6 h-6"
                        style={{ color: "#264a3e" }}
                      />
                    )}
                  </div>
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-500">
                      {complicatedCount > 0 ? "Complicated" : "Registered"}
                    </p>
                    <p
                      className={`text-lg font-semibold ${
                        complicatedCount > 0 ? "text-orange-600" : ""
                      }`}
                      style={complicatedCount === 0 ? { color: "#264a3e" } : undefined}
                    >
                      {complicatedCount > 0 ? complicatedCount : stats.registered}
                    </p>
                  </div>
                </div>
              </div> */}

              {/* In Transit */}
              {/* <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Truck className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-500">
                      In Transit
                    </p>
                    <p className="text-lg font-semibold text-yellow-600">
                      {filteredStats.Transit}
                    </p>
                  </div>
                </div>
              </div> */}

              {/* Delivered */}
              {/* <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-500">
                      Delivered
                    </p>
                    <p className="text-lg font-semibold text-green-600">
                      {filteredStats.Delivered}
                    </p>
                  </div>
                </div>
              </div> */}

              {/* Returned */}
              {/* <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <AlertCircle className="w-6 h-6 text-red-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-500">
                      Returned
                    </p>
                    <p className="text-lg font-semibold text-red-600">
                      {filteredStats.Returned}
                    </p>
                  </div>
                </div>
              </div> */}
            {/* </div>
          </div> */}
          {/* Bulk Action Bar - Only show if orders from THIS country are selected */}
          {selectedOrdersCompatibility.count > 0 && (
            <div
              className="px-4 sm:px-6 py-4 border-t border-b"
              style={{ backgroundColor: "#f0f9f4", borderColor: "#264a3e" }}
            >
              <div className="flex flex-col space-y-3">
                {/* Header: Selection Info and Clear Button */}
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center space-x-2 flex-wrap">
                    <CheckCircle
                      className="w-4 h-4 sm:w-5 sm:h-5"
                      style={{ color: "#264a3e" }}
                    />
                    <span
                      className="text-xs sm:text-sm font-medium cursor-default"
                      style={{ color: "#264a3e" }}
                    >
                      {selectedOrdersCompatibility.count} order
                      {selectedOrdersCompatibility.count !== 1
                        ? "s"
                        : ""}{" "}
                      selected
                    </span>
                    {/* View All Selection Button */}
                    <button
                      onClick={() => setViewAllSelectionModal(true)}
                      className="inline-flex items-center px-2 sm:px-3 py-1 text-xs rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                      title="View all selected orders"
                    >
                      <Eye className="w-3 h-3 sm:mr-1" />
                      <span className="hidden sm:inline">View All</span>
                    </button>
                  </div>

                  {/* Clear Selection */}
                  <button
                    onClick={() => {
                      // Deselect all orders for this country using the proper function
                      onToggleCountryOrdersSelection(country, false);
                    }}
                    className="text-xs sm:text-sm font-medium hover:opacity-80 whitespace-nowrap"
                    style={{ color: "#264a3e" }}
                  >
                    Clear Selection
                  </button>
                </div>

                {/* Bulk Shipping Progress Banner */}
                {bulkShippingProgress.isProcessing && Object.keys(bulkShippingProgress.orderStatus).length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-sm font-medium text-blue-900">
                          Bulk Shipping in Progress
                        </span>
                      </div>
                      <div className="flex items-center space-x-4 text-xs">
                        <span className="text-green-700 font-medium">
                          ✓ {Object.values(bulkShippingProgress.orderStatus).filter(s => s.status === "success").length} Success
                        </span>
                        <span className="text-red-700 font-medium">
                          ✗ {Object.values(bulkShippingProgress.orderStatus).filter(s => s.status === "error").length} Failed
                        </span>
                        <span className="text-gray-600">
                          {Object.values(bulkShippingProgress.orderStatus).filter(s => s.status === "pending" || s.status === "processing").length} Remaining
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                  {/* Bulk Actions */}
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 flex-wrap">
                    {selectedOrdersCompatibility.type === "ready-for-shipping" &&
                      country.toLowerCase() !== "rs" && (
                        <>
                          <button
                            onClick={handleBulkSendToShipping}
                            disabled={bulkShippingProgress.isProcessing}
                            className="inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md text-white bg-[#264a3e] hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                          >
                            <Send className="w-4 h-4 sm:mr-2" />
                            <span className="hidden lg:inline">
                              Send {selectedOrdersCompatibility.count} Order
                              {selectedOrdersCompatibility.count !== 1
                                ? "s"
                                : ""}{" "}
                              to Shipping
                            </span>
                            <span className="lg:hidden">Send to Shipping</span>
                          </button>
                        </>
                      )}

                    {selectedOrdersCompatibility.type ===
                      "ready-for-shipping" &&
                      country.toLowerCase() === "rs" && (
                        <button 
                          onClick={async () => {
                            const selectedOrdersList = getSelectedOrdersForCountry(country);
                            const orderIds = selectedOrdersList.map(order => order.id);
                            
                            if (orderIds.length === 0) return;
                            
                            const confirmed = window.confirm(
                              `Send ${orderIds.length} order${orderIds.length !== 1 ? 's' : ''} to BEX?\n\nThis will create shipments for selected orders.`
                            );
                            
                            if (!confirmed) return;
                            
                            setIsBulkProcessing(true);
                            
                            try {
                              let successCount = 0;
                              let failCount = 0;
                              
                              // Process each order sequentially
                              for (const orderId of orderIds) {
                                try {
                                  const response = await fetch(`${API_BASE_URL}/api/shipping/bex/send`, {
                                    method: "POST",
                                    headers: { "Content-Type": "application/json" },
                                    body: JSON.stringify({ orderId }),
                                  });
                                  
                                  const result = await response.json();
                                  
                                  if (result.success) {
                                    successCount++;
                                    console.log(`✅ [BULK BEX] Order ${orderId} sent successfully`);
                                    // Small delay between requests
                                    await new Promise(resolve => setTimeout(resolve, 300));
                                  } else {
                                    failCount++;
                                    console.error(`❌ [BULK BEX] Order ${orderId} failed:`, result.error);
                                  }
                                } catch (error) {
                                  failCount++;
                                  console.error(`❌ [BULK BEX] Order ${orderId} error:`, error);
                                }
                              }
                              
                              // Reload data for all affected countries
                              const affectedCountries = new Set<string>();
                              selectedOrdersList.forEach(order => {
                                affectedCountries.add(order.country?.toLowerCase() || '');
                              });
                              
                              for (const affectedCountry of Array.from(affectedCountries)) {
                                const targetPage = affectedCountry === country.toLowerCase() ? pagination.currentPage : 1;
                                await onLoadCountryDataWithFilters(affectedCountry, targetPage, {
                                  search: settings.search,
                                  sortBy: settings.sortBy,
                                  sortOrder: settings.sortOrder,
                                  statusFilter: settings.statusFilter,
                                  sourceFilter: settings.sourceFilter,
                                  periodFilter: settings.periodFilter,
                                });
                              }
                              
                              // Clear selection
                              onToggleCountryOrdersSelection(country, false);
                              
                              if (failCount > 0) {
                                alert(`BEX Bulk Send:\n✅ Success: ${successCount} orders\n❌ Failed: ${failCount} orders`);
                              }
                            } catch (error) {
                              console.error("❌ [BULK BEX] Error:", error);
                              alert("Error sending orders to BEX. Please try again.");
                            } finally {
                              setIsBulkProcessing(false);
                            }
                          }}
                          disabled={isBulkProcessing}
                          className="inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md text-white bg-[#264a3e] hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                        >
                          {isBulkProcessing ? (
                            <>
                              <svg className="animate-spin h-4 w-4 sm:mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <span className="hidden sm:inline">Sending...</span>
                              <span className="sm:hidden">Sending</span>
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 sm:mr-2" />
                              <span className="hidden lg:inline">
                                Send {selectedOrdersCompatibility.count} Order
                                {selectedOrdersCompatibility.count !== 1 ? "s" : ""} to Bex
                              </span>
                              <span className="lg:hidden">Send to Bex</span>
                            </>
                          )}
                        </button>
                      )}

                    {selectedOrdersCompatibility.type === "shipped" && (
                      <>
                        {/* Bulk Delete Button */}
                        <button
                          onClick={handleBulkDelete}
                          disabled={isBulkProcessing}
                          className="inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                        >
                          {isBulkProcessing ? (
                            <>
                              <RefreshCw className="w-4 h-4 sm:mr-2 animate-spin" />
                              <span className="hidden sm:inline">Deleting...</span>
                              <span className="sm:hidden">Deleting</span>
                            </>
                          ) : (
                            <>
                              <X className="w-4 h-4 sm:mr-2" />
                              <span className="hidden lg:inline">
                                Delete {selectedOrdersCompatibility.count}{" "}
                                Shipment
                                {selectedOrdersCompatibility.count !== 1
                                  ? "s"
                                  : ""}
                              </span>
                              <span className="lg:hidden">Delete Shipment{selectedOrdersCompatibility.count !== 1 ? "s" : ""}</span>
                            </>
                          )}
                        </button>

                        {/* Bulk Preview Button (no limit, hidden if pickup list issued) */}
                        {!selectedOrdersCompatibility.hasPickupList && (
                          <button
                            onClick={handleBulkPreview}
                            disabled={
                              isBulkProcessing ||
                              selectedOrdersCompatibility.count === 0
                            }
                            className="inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md text-white bg-[#264a3e] hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                            title={
                              selectedOrdersCompatibility.count === 0
                                ? "Please select at least 1 order"
                                : `Preview ${selectedOrdersCompatibility.count} label${selectedOrdersCompatibility.count !== 1 ? 's' : ''} (in batches of 9)`
                            }
                          >
                            <Eye className="w-4 h-4 sm:mr-2" />
                            <span className="hidden lg:inline">
                              Preview {selectedOrdersCompatibility.count} Label
                              {selectedOrdersCompatibility.count !== 1 ? "s" : ""}
                            </span>
                            <span className="lg:hidden">Preview Labels</span>
                          </button>
                        )}

                        {/* Show info message if pickup list is issued */}
                        {selectedOrdersCompatibility.hasPickupList && (
                          <div className="text-xs sm:text-sm text-gray-600">
                            <AlertCircle className="w-4 h-4 inline mr-1 text-blue-600" />
                            <span className="hidden sm:inline">
                              Selected orders have pickup lists issued - preview not available
                            </span>
                            <span className="sm:hidden">Pickup list issued - preview unavailable</span>
                          </div>
                        )}
                      </>
                    )}

                    {selectedOrdersCompatibility.type === "mixed" && (
                      isBulkProcessing ? (
                        <div className="text-xs sm:text-sm text-gray-600">
                          <svg className="animate-spin h-4 w-4 inline mr-1 text-[#264a3e]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span className="hidden sm:inline">Processing bulk operation...</span>
                          <span className="sm:hidden">Processing...</span>
                        </div>
                      ) : (
                        <div className="text-xs sm:text-sm text-gray-600">
                          <AlertTriangle className="w-4 h-4 inline mr-1 text-yellow-600" />
                          <span className="hidden sm:inline">
                            Selected orders are in different states. Please select
                            orders in the same state for bulk actions.
                          </span>
                          <span className="sm:hidden">Orders in different states</span>
                        </div>
                      )
                    )}
                  </div>
              </div>
            </div>
          )}


          {/* Orders Table/Cards */}
          {orders.length > 0 && (localViewMode === "cards" ? (
            /* Card View */
            <div className="p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {orders.map((order) => {
                  const isSelected = selectedOrders.has(order.id);
                  const isSending = sendingToShipping.has(order.id);
                  
                  // Get account information (same logic as table view)
                  let account: { id: number | null; username: string | null; email: string | null; level: string; team: string | null } | undefined;
                  
                  if (order.accountInfo && order.accountInfo.id !== null && order.accountInfo.username !== null) {
                    account = order.accountInfo;
                  } else if (order.user) {
                    // AlterCPA removed for Greek-CC
                    account = undefined; // getAccountById(altercpaState, order.user);
                  }
                  
                  // Get best timestamp for approved time
                  const approvedTimestamp = getBestTimestamp(order);
                  const approvedDate = approvedTimestamp ? new Date(approvedTimestamp) : null;
                  
                  // Get order date
                  const orderDate = parseDate(order.time);
                  
                  // Calculate total price
                  const pricing = getOrderPricing(order);
                  
                  // Get status badges
                  const altercpaStatus = getAlterCPAStatus(order);
                  const postOfficeStatus = getPostOfficeStatus(order);
                  
                  // Get bulk shipping status for this order
                  const bulkStatus = bulkShippingProgress.orderStatus[order.id];
                  const hasSuccessAnimation = successAnimations.has(order.id);
                  
                  return (
                    <div 
                      key={order.id} 
                      className={`border rounded-lg p-4 hover:shadow-lg transition-all ${
                        hasSuccessAnimation
                          ? "bg-green-200 border-green-400"
                          : bulkStatus?.status === "processing"
                          ? "bg-blue-50 border-blue-400 animate-pulse"
                          : bulkStatus?.status === "error"
                          ? "bg-red-100 border-red-400"
                          : isSelected 
                          ? "border-[#264a3e] border-2 bg-green-50" 
                          : "bg-white border-gray-200"
                      }`}
                      style={
                        hasSuccessAnimation
                          ? {
                              backgroundColor: "#bbf7d0",
                              transition: "background-color 3s ease-out",
                            }
                          : {}
                      }
                    >
                      {/* Header: Order ID and Checkbox */}
                      <div className="flex items-start justify-between mb-3 pb-3 border-b border-gray-200">
                        <div className="flex items-center space-x-2">
                          <span className="text-lg font-bold text-gray-900">#{order.id}</span>
                          
                          {/* Bulk Shipping Status Badge */}
                          {bulkStatus?.status === "processing" && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              <svg className="animate-spin h-3 w-3 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Processing
                            </span>
                          )}
                          {bulkStatus?.status === "error" && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800" title={bulkStatus.error}>
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Error
                            </span>
                          )}
                          
                          {order.trackingId && !bulkStatus && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Shipped
                            </span>
                          )}
                        </div>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => onToggleOrderSelection(order.id, order)}
                          className="h-5 w-5 border-gray-300 rounded flex-shrink-0 cursor-pointer"
                          style={{ accentColor: "#264a3e" }}
                        />
                      </div>
                      
                      {/* Customer Information */}
                      <div className="mb-3 space-y-2">
                        <div className="flex items-start space-x-2">
                          <User className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900 truncate">{order.name || "N/A"}</div>
                            {order.phone && (
                              <div className="text-xs text-gray-600 mt-0.5">
                                <Phone className="w-3 h-3 inline mr-1" />
                                {formatPhoneNumber(order.phone, country.toLowerCase() === "rs")}
                              </div>
                            )}
                            {order.email && (
                              <div className="text-xs text-gray-500 mt-0.5 truncate">
                                <Mail className="w-3 h-3 inline mr-1" />
                                {order.email}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Address */}
                        {(order.area || order.city || order.street || order.addr) && (
                          <div className="flex items-start space-x-2 pt-1">
                            <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                            <div className="text-xs text-gray-600 flex-1">
                              {order.area && <span>{order.area}, </span>}
                              {order.city && <span>{order.city}</span>}
                              {(order.index || order.zipcode) && order.city && <span>, {order.index || order.zipcode}</span>}
                              {(order.street || order.addr) && (
                                <div className="mt-0.5">{order.street || order.addr}</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Product Information */}
                      {order.offername && (
                        <div className="mb-3 pb-3 border-b border-gray-200">
                          <div className="text-sm font-medium text-gray-900 mb-1">{order.offername}</div>
                          <div className="flex items-center justify-between text-xs text-gray-600">
                            <span>Qty: {pricing.quantity}</span>
                            <span className="font-semibold text-gray-900">
                              {formatPriceValue(pricing.total, pricing.currency)}
                            </span>
                          </div>
                          {pricing.needsExtraShipping && (
                            <div className="mt-1 text-xs font-medium text-orange-700">
                              +{formatPriceValue(pricing.extraShippingFee, pricing.currency)} Shipping Cost
                            </div>
                          )}
                          {order.comment && (
                            <div className="text-xs text-gray-500 mt-1 italic">
                              Note: {order.comment}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Account Information */}
                      <div className="mb-3 pb-3 border-b border-gray-200 space-y-1.5">
                        {(() => {
                          // For manual orders
                          if (order.source === "manual" && order.createdBy) {
                            return (
                              <div className="flex items-center space-x-2">
                                <User className="w-4 h-4 text-gray-400" />
                                <div className="text-xs">
                                  <div className="font-medium text-gray-900">{order.createdBy.username || "N/A"}</div>
                                  <div className="text-gray-500">{order.createdBy.role || "Manual"}</div>
                                </div>
                              </div>
                            );
                          }
                          
                          // For AlterCPA orders
                          if (order.user && account) {
                            return (
                              <div className="flex items-center space-x-2">
                                <User className="w-4 h-4 text-gray-400" />
                                <div className="text-xs">
                                  <div className="font-medium text-gray-900">
                                    #{order.user} - {account.username || "N/A"}
                                  </div>
                                  {account.level && (
                                    <div className="text-gray-500">Level: {account.level}</div>
                                  )}
                                  {account.team && (
                                    <div className="text-gray-500">Team: {account.team}</div>
                                  )}
                                </div>
                              </div>
                            );
                          }
                          
                          return null;
                        })()}
                      </div>
                      
                      {/* Dates and Status */}
                      <div className="mb-3 space-y-2">
                        {orderDate && (
                          <div className="flex items-center space-x-2 text-xs">
                            <CalendarIcon className="w-4 h-4 text-gray-400" />
                            <div className="flex-1">
                              <span className="text-gray-500">Order Date: </span>
                              <span className="text-gray-900 font-medium">
                                {orderDate.toLocaleDateString()} {orderDate.toLocaleTimeString()}
                              </span>
                            </div>
                          </div>
                        )}
                        
                        {approvedDate && (
                          <div className="flex items-center space-x-2 text-xs">
                            <Clock className="w-4 h-4 text-gray-400" />
                            <div className="flex-1">
                              <span className="text-gray-500">Approved: </span>
                              <span className="text-gray-900 font-medium">
                                {approvedDate.toLocaleDateString()} {approvedDate.toLocaleTimeString()}
                              </span>
                            </div>
                          </div>
                        )}
                        
                        {/* Status Badges */}
                        <div className="flex items-center space-x-2 pt-1">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${altercpaStatus.color}`}
                          >
                            {altercpaStatus.name}
                          </span>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${postOfficeStatus.color}`}
                          >
                            {postOfficeStatus.name}
                          </span>
                        </div>
                        
                        {/* Tracking ID */}
                        {(() => {
                          // Check bulk progress first, then order tracking
                          const trackingFromBulk = bulkStatus?.trackingNumber;
                          const trackingNumber = trackingFromBulk || order.trackingId;
                          
                          if (trackingNumber) {
                            return (
                              <div className="flex items-center space-x-2 text-xs pt-1">
                                <Package className="w-4 h-4 text-blue-600" />
                                <div className="flex-1">
                                  <span className="text-gray-500">Tracking: </span>
                                  <span className="text-blue-600 font-medium">{trackingNumber}</span>
                                  {(order.shippingCompany || trackingFromBulk) && (
                                    <span className="text-gray-500 ml-1">
                                      ({order.shippingCompany?.toUpperCase() || (trackingFromBulk ? "ACS" : "")})
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          }
                          
                          return null;
                        })()}
                        
                        {/* Bulk Shipping Error */}
                        {bulkStatus?.status === "error" && bulkStatus.error && (
                          <div className="flex items-start space-x-2 text-xs pt-2 text-red-700 bg-red-50 p-2 rounded border border-red-200">
                            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <div>{bulkStatus.error}</div>
                          </div>
                        )}
                      </div>
                      
                      {/* Action buttons for card view */}
                      <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-200">
                        {country.toLowerCase() === "rs" && isConnected && !order.trackingId && (
                          <button
                            onClick={async () => {
                              try {
                                if (!order.area || !order.city || !order.addr) {
                                  alert("Please edit the order first to complete the BEX address.");
                                  return;
                                }
                                setIsBulkProcessing(true);
                                const response = await fetch(`${API_BASE_URL}/api/shipping/bex/send`, {
                                  method: "POST",
                                  headers: { "Content-Type": "application/json" },
                                  body: JSON.stringify({ orderId: order.id }),
                                });
                                const result = await response.json();
                                if (result.success) {
                                  await onLoadCountryDataWithFilters(country, pagination.currentPage, {
                                    search: settings.search,
                                    sortBy: settings.sortBy,
                                    sortOrder: settings.sortOrder,
                                    statusFilter: settings.statusFilter,
                                    sourceFilter: settings.sourceFilter,
                                    periodFilter: settings.periodFilter,
                                  });
                                } else {
                                  alert(`Failed to send to BEX: ${result.error || "Unknown error"}`);
                                }
                              } catch (error) {
                                console.error("Error sending to BEX:", error);
                                alert("Failed to send to BEX. Please try again.");
                              } finally {
                                setIsBulkProcessing(false);
                              }
                            }}
                            disabled={isSending || isBulkProcessing}
                            className="flex items-center space-x-1 text-white px-3 py-1.5 rounded text-xs hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            style={{ backgroundColor: "#264a3e" }}
                          >
                            {isSending || isBulkProcessing ? (
                              <RefreshCw className="w-3 h-3 animate-spin" />
                            ) : (
                              <Send className="w-3 h-3" />
                            )}
                            <span>Send to Bex</span>
                          </button>
                        )}
                        {country.toLowerCase() !== "rs" && (() => {
                          // Create a modified order with tracking from bulk progress if available
                          const trackingFromBulk = bulkStatus?.trackingNumber;
                          const orderWithTracking = trackingFromBulk ? {
                            ...order,
                            trackingId: trackingFromBulk,
                            shippingCompany: "acs",
                          } : order;
                          
                          return (
                            <PostOfficeSelector
                              order={orderWithTracking}
                              onSelectPostOffice={onSelectPostOffice}
                              onClearPostOffice={onClearPostOffice}
                              onSendToShipping={handleSendToShipping}
                              onPrintLabel={onPrintLabel}
                              onViewLabel={onViewLabel}
                              onDeleteShipment={onDeleteShipment}
                              isLoading={isSending}
                            />
                          );
                        })()}
                        {!order.trackingId && !bulkStatus?.trackingNumber && !order.selectedPostOffice && (
                          <button
                            onClick={() => setEditModal({ isOpen: true, order })}
                            className="flex items-center space-x-1 text-gray-600 hover:text-[#264a3e] px-2 py-1.5 rounded text-xs transition-colors"
                            title="Edit order"
                          >
                            <Edit className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            /* Table View */
            <div
              className="overflow-x-auto border border-gray-200 rounded-lg w-full relative overflow-hidden"
              style={{ minHeight: "500px" }}
            >
            {/* Loading overlay */}
            {pagination.loading && (
              <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                <div className="flex items-center space-x-2">
                  <RefreshCw className="w-5 h-5 animate-spin text-[#264a3e]" />
                  <span className="text-sm text-gray-600">Loading orders...</span>
                </div>
              </div>
            )}
            <table
              className="min-w-full divide-y divide-gray-200"
              style={{ minWidth: "1400px" }}
            >
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={
                        orders.length > 0 &&
                        orders.every((order) => selectedOrders.has(order.id))
                      }
                      onChange={(e) => {
                        onToggleCountryOrdersSelection(
                          country,
                          e.target.checked
                        );
                      }}
                      className="h-4 w-4 border-gray-300 rounded"
                      style={{
                        accentColor: "#264a3e",
                        color: "#264a3e",
                      }}
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span style={{ minWidth: "65px" }}>ORDER ID</span>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[250px]">
                    <span>CUSTOMER</span>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px]">
                    GOODS (PAID)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    SHIPMENT
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    TOTAL COST
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[150px]">
                    COMMENT
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    TRACKING NUMBER
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    TRACKING STATUS
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ACTIONS
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(settings.orders || []).map((order) => {
                  // Use accountInfo from order (backend enrichment) if available, otherwise lookup from Redux
                  // accountInfo has structure: { id, username, email, level, team }
                  let account: { id: number | null; username: string | null; email: string | null; level: string; team: string | null } | undefined;
                  
                  if (order.accountInfo && order.accountInfo.id !== null && order.accountInfo.username !== null) {
                    // Use backend-enriched accountInfo if it has valid data
                    account = order.accountInfo;
                  } else if (order.user) {
                    // Fallback removed - AlterCPA not available in Greek-CC
                    account = undefined; // getAccountById(altercpaState, order.user);
                  }
                  const isSelected = selectedOrders.has(order.id);
                  const isSending = sendingToShipping.has(order.id);
                  const pricing = getOrderPricing(order);

                  // Check if this order can be selected (stage compatibility for individual selection)
                  let isSelectable = true;
                  let disableReason = "";

                  if (selectedOrders.size > 0 && !isSelected) {
                    const orderStage = getOrderStage(order);
                    
                    if (currentSelectionStage !== null && currentSelectionStage !== orderStage) {
                      isSelectable = false;
                      disableReason = "Orders must be in the same stage. Open stage selection modal to choose which stage to keep.";
                    }
                  }

                  // Get bulk shipping status for this order
                  const bulkStatus = bulkShippingProgress.orderStatus[order.id];
                  const hasSuccessAnimation = successAnimations.has(order.id);

                  return (
                    <tr
                      key={order.id}
                      className={`${!isConnected ? "bg-red-25" : ""} ${
                        hasSuccessAnimation
                          ? "bg-green-200 transition-all duration-3000 ease-out"
                          : bulkStatus?.status === "processing"
                          ? "bg-blue-50 animate-pulse"
                          : bulkStatus?.status === "error"
                          ? "bg-red-100"
                          : isSelected
                          ? "border-l-4"
                          : !isSelectable
                          ? "bg-gray-50 opacity-60"
                          : "hover:bg-gray-50"
                      } transition-colors`}
                      style={
                        hasSuccessAnimation
                          ? {
                              backgroundColor: successAnimations.has(order.id) ? "#bbf7d0" : "transparent",
                              transition: "background-color 3s ease-out",
                            }
                          : isSelected && !bulkStatus
                          ? {
                              backgroundColor: "#f0f9f4",
                              borderLeftColor: "#264a3e",
                            }
                          : {}
                      }
                      title={!isSelectable ? disableReason : ""}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => onToggleOrderSelection(order.id, order)}
                          disabled={!isSelectable}
                          className="h-4 w-4 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{
                            accentColor: "#264a3e",
                            color: "#264a3e",
                          }}
                        />
                      </td>
                      {/* Order ID */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <div>
                            <div className="text-sm font-medium text-gray-900">#{order.id}</div>
                            {order.source === "web" && order.originalOrderId && (
                              <div className="text-xs text-gray-500">Web: {order.originalOrderId}</div>
                            )}
                          </div>
                          
                          {/* Bulk Shipping Status Indicator */}
                          {bulkStatus && (
                            <div className="flex-shrink-0">
                              {bulkStatus.status === "processing" && (
                                <div className="flex items-center text-blue-600" title="Processing...">
                                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                </div>
                              )}
                              {bulkStatus.status === "success" && (
                                <div className="flex items-center text-green-600" title={`Success: ${bulkStatus.trackingNumber || ""}`}>
                                  <CheckCircle className="h-5 w-5" />
                                </div>
                              )}
                              {bulkStatus.status === "error" && (
                                <div className="flex items-center text-red-600" title={bulkStatus.error || "Error"}>
                                  <AlertCircle className="h-5 w-5" />
                                </div>
                              )}
                              {bulkStatus.status === "pending" && (
                                <div className="flex items-center text-gray-400" title="Pending...">
                                  <Clock className="h-5 w-5" />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Error message below order ID if error occurred */}
                        {bulkStatus?.status === "error" && bulkStatus.error && (
                          <div className="text-xs text-red-600 mt-1 max-w-xs">
                            {bulkStatus.error}
                          </div>
                        )}
                      </td>

                      {/* Customer */}
                      <td className="px-6 py-4">
                        {(() => {
                          const webData = getWebOrderData(order);
                          
                          if (webData) {
                            // Web order - show customer with shipping address first (more important for delivery)
                            return (
                              <div className="space-y-2">
                                <div>
                                  <div className="text-sm font-semibold text-gray-900">{webData.customer.name}</div>
                                  <div className="text-xs text-gray-600">{webData.customer.email}</div>
                                  <div className="text-xs text-gray-600">{webData.customer.phone}</div>
                                </div>
                                
                                {webData.customer.addressesAreSame ? (
                                  <div className="border-t pt-2 mt-2">
                                    <div className="text-xs font-medium text-gray-700 mb-1">Shipping & Billing Address:</div>
                                    <div className="text-xs text-gray-600">
                                      {webData.customer.billingAddress.address1}
                                      {webData.customer.billingAddress.address2 && `, ${webData.customer.billingAddress.address2}`}
                                      <br />
                                      {webData.customer.billingAddress.city}, {webData.customer.billingAddress.postcode}
                                      <br />
                                      {webData.customer.billingAddress.zone}, {webData.customer.billingAddress.country}
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <div className="border-t pt-2 mt-2">
                                      <div className="text-xs font-medium text-gray-700 mb-1">Shipping Address:</div>
                                      <div className="text-xs text-gray-600">
                                        {webData.customer.shippingAddress && (
                                          <>
                                            {webData.customer.shippingAddress.address1}
                                            {webData.customer.shippingAddress.address2 && `, ${webData.customer.shippingAddress.address2}`}
                                            <br />
                                            {webData.customer.shippingAddress.city}, {webData.customer.shippingAddress.postcode}
                                            <br />
                                            {webData.customer.shippingAddress.zone}, {webData.customer.shippingAddress.country}
                                          </>
                                        )}
                                      </div>
                                    </div>

                                    <div className="border-t pt-2 mt-2">
                                      <div className="text-xs font-medium text-gray-700 mb-1">Billing Address:</div>
                                      <div className="text-xs text-gray-600">
                                        {webData.customer.billingAddress.address1}
                                        {webData.customer.billingAddress.address2 && `, ${webData.customer.billingAddress.address2}`}
                                        <br />
                                        {webData.customer.billingAddress.city}, {webData.customer.billingAddress.postcode}
                                        <br />
                                        {webData.customer.billingAddress.zone}, {webData.customer.billingAddress.country}
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                            );
                          }
                          
                          // Manual/AlterCPA order - show simple customer info
                          return (
                            <div className="text-sm text-gray-900 break-words">
                              <div className="text-sm font-medium text-gray-900 break-words mb-2">
                                {order.name || "N/A"}
                              </div>
                              <div className="flex items-center mb-1">
                                <Phone className="w-3 h-3 text-gray-400 mr-1" />
                                <span className="font-mono text-sm">
                                  {formatPhoneNumber(order.phone, country.toLowerCase() === "rs")}
                                </span>
                              </div>
                              {order.email && (
                                <div className="flex items-center">
                                  <Mail className="w-3 h-3 text-gray-400 mr-1" />
                                  <span className="text-xs text-gray-500 break-words">
                                    {order.email}
                                  </span>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </td>

                      {/* Goods (PAID) */}
                      <td className="px-6 py-4">
                        {(() => {
                          const webData = getWebOrderData(order);
                          
                          if (webData && webData.paidItems.length > 0) {
                            // Web order - show paid items
                            return (
                              <div className="space-y-1">
                                {webData.paidItems.map((item: any, idx: number) => (
                                  <div key={idx} className="text-xs">
                                    <div className="font-medium text-gray-900">{item.productName}</div>
                                    <div className="text-gray-600">
                                      Qty: {item.quantity} × {formatCurrency(item.unitPrice, webData.currency)} = {formatCurrency(item.total, webData.currency)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            );
                          }
                          
                          // Manual/AlterCPA order - show simple product info
                          return (
                            <div className="text-sm font-medium text-gray-900 break-words">
                              {order.offername || "N/A"}
                              <div className="text-xs text-gray-500 mt-1">
                                Qty: {pricing.quantity} • {formatPriceValue(pricing.unitPrice, pricing.currency)} each
                              </div>
                            </div>
                          );
                        })()}
                      </td>

                      {/* Shipment */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          const webData = getWebOrderData(order);
                          
                          if (webData) {
                            return (
                              <div className="flex items-center gap-1 text-sm">
                                {webData.shippingCost === 0 ? (
                                  <span className="text-green-600 font-medium">Free Shipping</span>
                                ) : (
                                  <span className="text-gray-900">{formatCurrency(webData.shippingCost, webData.currency)}</span>
                                )}
                              </div>
                            );
                          }
                          
                          // Manual/AlterCPA order - show extra shipping if applicable
                          if (pricing.needsExtraShipping) {
                            return (
                              <div className="text-sm font-semibold text-orange-700">
                                +{formatPriceValue(pricing.extraShippingFee, pricing.currency)}
                              </div>
                            );
                          }
                          
                          return <span className="text-xs text-gray-400">—</span>;
                        })()}
                      </td>

                      {/* Total Cost */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          const webData = getWebOrderData(order);
                          
                          if (webData) {
                            return (
                              <span className="text-sm font-bold text-gray-900">
                                {formatCurrency(webData.totalCost, webData.currency)}
                              </span>
                            );
                          }
                          
                          // Manual/AlterCPA order - show pricing total
                          return (
                            <span className="text-sm font-bold text-gray-900">
                              {formatPriceValue(pricing.total, pricing.currency)}
                            </span>
                          );
                        })()}
                      </td>

                      {/* Comment */}
                      <td className="px-6 py-4">
                        {(() => {
                          // For web orders, show comment_for_courier
                          if (order.source === 'web' && order.webOrderData?.comment_for_courier) {
                            return (
                              <div className="text-xs text-gray-700 break-words">
                                {order.webOrderData.comment_for_courier}
                              </div>
                            );
                          }
                          
                          // For manual orders, show comment if it's not auto-generated
                          if (order.source !== 'web' && order.comment && !order.comment.includes('Payment:')) {
                            return (
                              <div className="text-xs text-gray-700 break-words">
                                {order.comment}
                              </div>
                            );
                          }
                          
                          return <span className="text-xs text-gray-400">—</span>;
                        })()}
                      </td>

                      {/* Tracking Number */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          // Check if we have a tracking number from bulk shipping progress first
                          const trackingFromBulk = bulkStatus?.trackingNumber;
                          const trackingNumber = trackingFromBulk || order.trackingId;
                          
                          if (trackingNumber) {
                            return (
                              <div>
                                <div className="text-sm font-mono text-gray-900">{trackingNumber}</div>
                                <div className="text-xs text-gray-500">
                                  {order.shippingCompany?.toUpperCase() || (trackingFromBulk ? "ACS" : "")}
                                </div>
                              </div>
                            );
                          }
                          
                          return <span className="text-xs text-gray-400 italic">Not shipped</span>;
                        })()}
                      </td>

                      {/* TRACKING STATUS */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getACSTrackingStatusBadge(order) || (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      {/* Actions Column */}
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {selectedOrders.size === 0 ? (
                          // Normal mode - show action buttons
                          <div className="flex items-center gap-2">
                            {(() => {
                              // Check if order has tracking (from bulk progress or actual order data)
                              const trackingFromBulk = bulkStatus?.trackingNumber;
                              const hasTracking = trackingFromBulk || order.trackingId;
                              
                              return (
                                <>
                                  {/* Edit Shipping Address - for web orders only, before shipping */}
                                  {order.source === "web" && !hasTracking && (
                                    <button
                                      onClick={() => setEditModal({ isOpen: true, order })}
                                      className="text-blue-600 hover:text-blue-800 transition-colors"
                                      title="Edit Shipping Address"
                                    >
                                      <Edit className="w-4 h-4" />
                                    </button>
                                  )}

                                  {/* Send to ACS or View/Print/Delete if already shipped */}
                                  {!hasTracking ? (
                              <button
                                onClick={() => {
                                  // Send to ACS with today's date
                                  setPickupDateModal({
                                    isOpen: true,
                                    orderId: order.id,
                                    orderIds: null,
                                    shippingCompany: "acs",
                                    isBulk: false,
                                    orders: [{
                                      id: order.id,
                                      price: order.price || 0,
                                      currency: order.currency || 'EUR',
                                      source: order.source
                                    }]
                                  });
                                }}
                                disabled={isSending}
                                className="flex items-center space-x-1 text-white px-3 py-1 rounded text-xs hover:opacity-90 disabled:opacity-50 transition-colors"
                                style={{ backgroundColor: "#264a3e" }}
                                title="Send to ACS"
                              >
                                {isSending ? (
                                  <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                                ) : (
                                  <Send className="w-3 h-3" />
                                )}
                                <span>Send to ACS</span>
                              </button>
                            ) : (
                              <>
                                {/* View Tracking */}
                                <button
                                  onClick={() => onViewLabel(order.id, "a6")}
                                  className="text-purple-600 hover:text-purple-800 transition-colors"
                                  title="View Tracking"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>

                                {/* Print Voucher */}
                                <button
                                  onClick={() => onPrintLabel(order.id, "a6")}
                                  className="text-blue-600 hover:text-blue-800 transition-colors"
                                  title="Print Voucher"
                                >
                                  <Package className="w-4 h-4" />
                                </button>

                                {/* Delete Shipment */}
                                <button
                                  onClick={() => {
                                    if (window.confirm(`Delete shipment for order #${order.id}?`)) {
                                      onDeleteShipment(order.id);
                                    }
                                  }}
                                  className="text-red-600 hover:text-red-800 transition-colors"
                                  title="Delete Shipment"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </>
                        );
                      })()}
                          </div>
                        ) : isSelected ? (
                          // Bulk selection mode - selected row
                          <div className="flex items-center justify-center">
                            <CheckCircle
                              className="w-5 h-5"
                              style={{ color: "#264a3e" }}
                            />
                            <span
                              className="text-xs font-medium ml-2"
                              style={{ color: "#264a3e" }}
                            >
                              Selected
                            </span>
                          </div>
                        ) : (
                          // Bulk selection mode - not selected row (disabled)
                          <div className="flex items-center justify-center">
                            <span className="text-xs text-gray-400 italic">
                              Actions disabled
                            </span>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          ))}

          {/* Country Footer */}
          {(settings.orders || []).length === 0 && !pagination.loading && (
            <div className="px-6 py-8 text-center">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">
                No packaging for {country.toUpperCase()} of the selected period
              </p>
            </div>
          )}

          {/* Country Pagination Controls - Always show when there are results or loading */}
          {(pagination.totalPages > 0 || pagination.loading) && (
            <div className="px-4 sm:px-6 py-4 border-t border-gray-200 bg-gray-50 w-full">
              {pagination.loading ? (
                <div className="flex items-center justify-center py-4">
                  <RefreshCw className="w-5 h-5 animate-spin text-[#264a3e] mr-2" />
                  <span className="text-sm text-gray-600">Loading...</span>
                </div>
              ) : (
                <Pagination
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  totalItems={pagination.totalCount}
                  itemsPerPage={50}
                  onPageChange={(page) => onCountryPageChange(country, page)}
                  showFirstLast={true}
                  maxVisiblePages={5}
                />
              )}
            </div>
          )}
        </>
      )}

      {/* Edit Order Modal - BEX removed for Greek-CC (Greece uses ACS only) */}
      <EditOrderModal
        isOpen={editModal.isOpen}
        onClose={() => setEditModal({ isOpen: false, order: null })}
        order={editModal.order}
        onSave={onEditOrder}
      />

      {/* Pickup Date Modal */}
      <PickupDateModal
        isOpen={pickupDateModal.isOpen}
        onClose={() =>
          setPickupDateModal({
            isOpen: false,
            orderId: null,
            orderIds: null,
            shippingCompany: "",
            isBulk: false,
          })
        }
        orderId={pickupDateModal.orderId || 0}
        shippingCompany={pickupDateModal.shippingCompany}
        isBulk={pickupDateModal.isBulk}
        bulkCount={pickupDateModal.orderIds?.length || 0}
        orders={pickupDateModal.orders || []}
        onConfirm={async (pickupDate, notes, priceRounding) => {
          if (pickupDateModal.isBulk && pickupDateModal.orderIds) {
            // Bulk shipping - Show progress inline in table
            console.log(
              `🚚 [BULK-ASYNC] Starting async bulk shipment for ${pickupDateModal.orderIds.length} orders`
            );
            
            const orderIds = pickupDateModal.orderIds;
            
            // Close pickup date modal
            setPickupDateModal({
              isOpen: false,
              orderId: null,
              orderIds: null,
              shippingCompany: "",
              isBulk: false,
            });
            
            // Initialize progress state
            const initialStatus: Record<number, any> = {};
            orderIds.forEach(orderId => {
              initialStatus[orderId] = { status: "pending" };
            });
            
            setBulkShippingProgress({
              isProcessing: true,
              orderStatus: initialStatus,
            });
            
            // Start the bulk shipping process
            startBulkShippingAsync(orderIds, pickupDate, notes, priceRounding);
            
            return;
          }
          
          // Single order shipping
          if (pickupDateModal.orderId) {
            // Single order shipping
            if (pickupDateModal.shippingCompany === "bex") {
              onSendToBex(pickupDateModal.orderId, pickupDate, notes);
            } else {
              onSendToShipping(
                pickupDateModal.orderId,
                pickupDateModal.shippingCompany,
                pickupDate,
                notes
              );
            }
          }
          setPickupDateModal({
            isOpen: false,
            orderId: null,
            orderIds: null,
            shippingCompany: "",
            isBulk: false,
            orders: undefined,
          });
        }}
      />

      {/* View All Selection Modal */}
      <ViewAllSelectionModal
        isOpen={viewAllSelectionModal}
        onClose={() => setViewAllSelectionModal(false)}
        selectedOrders={getSelectedOrdersForCountry(country)}
        onClearSelection={() => onToggleCountryOrdersSelection(country, false)}
      />
    </div>
  );
};

export default CountrySection;
